"use client";

import { IBaseLabelValue } from "@/interfaces";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import React, { useRef, useState } from "react";
import { IRawPurchasedElectricityHistoryInquiryResData } from "@/interfaces/inquiries/governmental";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { useSession } from "next-auth/react";
import { usePayFinesMutation } from "@/hooks";
import { useAppStore } from "@/store";

const ElectricityBillHistoryInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedElectricityHistoryInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const { data: session } = useSession();
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const router = useRouter();
  const response = data.data.data;
  const [openAccordionId, setOpenAccordionId] = useState<number>(0);
  if (!response) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          برای این شناسه قبضی صادر نشده
        </div>
      </div>
    );
  }
  const { bill_id } = data.data.input;

  const inputContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.inputLabel"),
      value: bill_id || "",
    },
  ];
  const formatPrice = (value?: string | number) =>
    value ? `${formatNumberWithDots(Number(value) / 10)} تومان` : "-";

  const billItemResultContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.companyName"),
      value: "companyName",
    },
    {
      label: t("electricityBill.customerName"),
      value: "customerName",
    },
    {
      label: t("electricityBill.customerFamily"),
      value: "customerFamily",
    },
    {
      label: t("electricityBill.customerType"),
      value: "customerType",
    },
    {
      label: t("electricityBill.address"),
      value: "address",
    },
    {
      label: t("electricityBill.postalCode"),
      value: "postalCode",
    },
    {
      label: t("electricityBill.fileNumber"),
      value: "fileNumber",
    },
    {
      label: t("electricityBill.identificationNumber"),
      value: "identificationNumber",
    },
    {
      label: t("electricityBill.tariffType"),
      value: "tariffType",
    },
    {
      label: t("electricityBill.phase"),
      value: "phase",
    },
    {
      label: t("electricityBill.amper"),
      value: "amper",
    },
    {
      label: t("electricityBill.voltageType"),
      value: "voltageType",
    },
    {
      label: t("electricityBill.contractDemand"),
      value: "contractDemand",
    },
    {
      label: t("electricityBill.year"),
      value: "year",
    },
    {
      label: t("electricityBill.period"),
      value: "period",
    },
    {
      label: t("electricityBill.previousReadingDate"),
      value: "previousReadingDate",
    },
    {
      label: t("electricityBill.currentReadingDate"),
      value: "currentReadingDate",
    },
    {
      label: t("electricityBill.billExportationDate"),
      value: "billExportationDate",
    },
    {
      label: t("electricityBill.rejectDate"),
      value: "rejectDate",
    },
    {
      label: t("electricityBill.normalConsumption"),
      value: "normalConsumption",
    },
    {
      label: t("electricityBill.peakConsumption"),
      value: "peakConsumption",
    },
    {
      label: t("electricityBill.lowConsumption"),
      value: "lowConsumption",
    },
    {
      label: t("electricityBill.fridayConsumption"),
      value: "fridayConsumption",
    },
    {
      label: t("electricityBill.reactiveConsumption"),
      value: "reactiveConsumption",
    },
    {
      label: t("electricityBill.demandRead"),
      value: "demandRead",
    },
    {
      label: t("electricityBill.averageConsumption"),
      value: "averageConsumption",
    },
    {
      label: t("electricityBill.billPayableAmount"),
      value: "billPayableAmount",
      type: "price",
    },
    {
      label: t("electricityBill.periodAmount"),
      value: "periodAmount",
      type: "price",
    },
    {
      label: t("electricityBill.insuranceAmount"),
      value: "insuranceAmount",
      type: "price",
    },
    {
      label: t("electricityBill.taxAmount"),
      value: "taxAmount",
      type: "price",
    },
    {
      label: t("electricityBill.payTollAmount"),
      value: "paytollAmount",
      type: "price",
    },
    {
      label: t("electricityBill.electricityTaxAmount"),
      value: "electricityTaxAmount",
      type: "price",
    },
    {
      label: t("electricityBill.previousDebt"),
      value: "previousDebt",
      type: "price",
    },
    {
      label: t("electricityBill.energyAmount"),
      value: "energyAmount",
      type: "price",
    },
    {
      label: t("electricityBill.reactiveAmount"),
      value: "reactiveAmount",
      type: "price",
    },
    {
      label: t("electricityBill.demandAmount"),
      value: "demandAmount",
      type: "price",
    },
    {
      label: t("electricityBill.subscriptionAmount"),
      value: "subscriptionAmount",
      type: "price",
    },
    {
      label: t("electricityBill.seasonAmount"),
      value: "seasonAmount",
      type: "price",
    },
    {
      label: t("electricityBill.freeAmount"),
      value: "freeAmount",
      type: "price",
    },
    {
      label: t("electricityBill.gasDiscountAmount"),
      value: "gasDiscountAmount",
      type: "price",
    },
    {
      label: t("electricityBill.discountAmount"),
      value: "discountAmount",
      type: "price",
    },
    {
      label: t("electricityBill.warmDaysCount"),
      value: "warmDaysCount",
    },
    {
      label: t("electricityBill.coldDaysCount"),
      value: "coldDaysCount",
    },
    {
      label: t("electricityBill.totalDaysCount"),
      value: "totalDaysCount",
    },
    {
      label: t("electricityBill.consumptionDebtAmount"),
      value: "consumptionDebtAmount",
      type: "price",
    },
    {
      label: t("electricityBill.otherDebtAmount"),
      value: "otherDebtAmount",
      type: "price",
    },
    {
      label: t("electricityBill.branchDebtAmount"),
      value: "branchDebtAmount",
      type: "price",
    },
    {
      label: t("electricityBill.amount"),
      value: "amount",
      type: "price",
    },
  ];

  const getBillValue = (item: any, key: string | number, type?: string) => {
    if (type === "price") {
      return formatPrice(item[key]);
    } else {
      return item[key] ?? "-";
    }
  };

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  // const handlePayBillSubmission = () => {
  //   const payload: IPayFinesPayload = {
  //     amount: response.amount,
  //     callback_url: generatePaymentCallbackUrl(pathName),
  //     bill_id: Number(response.billID),
  //     pay_id: Number(response.payID),
  //   };
  //   PayFineFn(
  //     { payload },
  //     {
  //       onSuccess: (data) => {
  //         if (data.data) {
  //           setPreviousPath(pathName);
  //           window.location.href = data.data?.payment_link;
  //         }
  //       },
  //     },
  //   );
  // };
  const buildPdfTableData = () => {
    const tableRows = [];

    tableRows.push(["", ""]);

    response.bills.forEach((bill, idx) => {
      tableRows.push([`قبض شماره ${idx + 1}`, ""]); // section header
      billItemResultContent.forEach((item) => {
        tableRows.push([
          item.label,
          getBillValue(bill.info, item.value, item.type),
        ]);
      });
      tableRows.push(["", ""]); // spacer
    });

    return tableRows;
  };
  return (
    <>
      <div className="shadow-main relative basis-[500px] rounded-2xl bg-linear-to-b from-rose-500 to-transparent p-[2px] lg:basis-[640px]">
        <FlexCol className="items-stretch rounded-2xl bg-white p-8">
          <h2 className="mb-8 text-center font-bold lg:text-lg">
            استعلام تاریخچه قبض برق
          </h2>

          <InputDataContent content={inputContent} />
          <FlexBetween className="mb-3 items-center">
            <h2 className="text-center text-lg font-bold">
              {t("postalCodeInquiry.resultTitle")}
            </h2>
            <DownloadPdf
              fileName={`تاریخچه قبوض برق - ${bill_id}`}
              tableTitle="تاریخچه قبوض برق"
              resultContent={buildPdfTableData()}
            />
          </FlexBetween>
          <ul
            className={cn(
              "shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8",
            )}
          >
            <div className="mb-3 flex items-center text-sm md:text-base">
              <span>تعداد کل قبوض :</span>
              <span className="mx-1">{response.bills.length} عدد</span>
            </div>
            <div className="max-h-[500px] overflow-x-hidden overflow-y-auto">
              {response.bills.map((billItem, i) => (
                <Accordion
                  key={i}
                  title={`${formatNumberWithDots(Number(billItem.amount) / 10)} تومان `}
                  open={i === openAccordionId}
                  hasDetails
                  className="mb-2"
                  onOpenChange={(isOpen) => {
                    setOpenAccordionId(isOpen ? i : 0);
                  }}
                  details={
                    <>
                      <p className="text-sm">تاریخ صدور</p>
                      <p className="text-sm" dir="ltr">
                        {billItem.info.billExportationDate}
                      </p>
                    </>
                  }
                >
                  <div
                    className={cn(
                      "shadow-main mb-10 flex flex-col gap-4 rounded-xl p-4",
                    )}
                  >
                    <ul className="flex flex-col gap-4">
                      {billItemResultContent.map((item, index) => (
                        <li
                          key={index}
                          className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm"
                        >
                          <span className="min-w-max">{item.label}</span>
                          <div className="text-primary-main flex items-center gap-2 text-start">
                            {getBillValue(billItem.info, item.value, item.type)}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {/*{warning.finalPrice !== "0" && (*/}
                    {/*  <CustomButton*/}
                    {/*    onClick={() =>*/}
                    {/*      handlePayFineSubmission({*/}
                    {/*        paperId: Number(warning.paperId),*/}
                    {/*        paymentId: Number(warning.paymentId),*/}
                    {/*        inquirePrice: warning.finalPrice,*/}
                    {/*      })*/}
                    {/*    }*/}
                    {/*    variant="outline"*/}
                    {/*    className={cn(*/}
                    {/*      "text-success-main border-success-main hover:shadow-main hover:text-success-main mx-auto w-max px-2",*/}
                    {/*    )}*/}
                    {/*  >*/}
                    {/*      <span className="text-sm">*/}
                    {/*        {t("fines.warningDTOs.payThisFine")}*/}
                    {/*      </span>*/}
                    {/*  </CustomButton>*/}
                    {/*)}*/}
                  </div>
                </Accordion>
              ))}
            </div>
            {/*{response?.amount != 0 ? (*/}
            {/*  <CustomButton*/}
            {/*    id="details-water-inquiry-back"*/}
            {/*    className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}*/}
            {/*    onClick={handlePayBillSubmission}*/}
            {/*    isLoading={isPaySubmitting}*/}
            {/*  >*/}
            {/*    پرداخت {formatPrice(response.amount)}*/}
            {/*  </CustomButton>*/}
            {/*) : (*/}
            {/*  <p className="bg-success-main mx-auto w-full max-w-[200px] rounded-lg px-3 py-2 text-center text-white">*/}
            {/*    تسویه شده*/}
            {/*  </p>*/}
            {/*)}*/}
          </ul>
          <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
            <DownloadPdf
              id="electricity-inquiry-download-pdf"
              fileName={`تاریخچه قبوض برق - ${bill_id}`}
              tableTitle="تاریخچه قبوض برق"
              resultContent={buildPdfTableData()}
              className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
              showIcon={false}
              labelClass="text-xs lg:text-sm xl:text-base font-medium"
            />
            <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
              <CustomButton
                id="details-electricity-inquiry-back"
                className={cn("w-fit bg-gray-500")}
                onClick={() =>
                  router.push(routes.main.INQUIRY_HISTORY.primaryPath)
                }
              >
                {t("companyInformationInquiry.backButton")}
              </CustomButton>
              <CustomButton
                id="details-electricity-inquiry-new-request"
                onClick={() =>
                  router.push(
                    routes.services.Electricity_Bill_History_inquiry
                      .rewritePath,
                  )
                }
                className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
              >
                {t("newRequest")}
              </CustomButton>
            </div>
          </FlexCenter>
        </FlexCol>
      </div>
    </>
  );
};

export default ElectricityBillHistoryInquiryDetails;
